import { useRef, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Link, NavLink } from "@/components/LocalizedLink";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ChevronDown, Menu, X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { CERNIO_URL, languagePath, localizedPath } from "@/site";

const linkBase =
  "focus-ring font-mono text-[12px] uppercase tracking-[0.18em] transition-colors duration-300";
function LangSwitch({ mobile = false }) {
  const { lang } = useLang();
  const { pathname, hash, search } = useLocation();
  return (
    <div
      className="flex items-center gap-1"
      aria-label="Language"
      data-testid={mobile ? "lang-switch-mobile" : "lang-switch"}
    >
      {[
        ["en", "English", "gb"],
        ["tr", "Türkçe", "tr"],
      ]
        .filter(([code]) => code !== lang)
        .map(([code, label, flag]) => (
          <RouterLink
            key={code}
            to={localizedPath(pathname + search + hash, code)}
            aria-label={label}
            title={label}
            lang={code}
            hrefLang={code}
            data-testid={`lang-switch-${code}${mobile ? "-mobile" : ""}`}
            className="focus-ring grid h-10 w-10 place-items-center border border-transparent transition-colors hover:border-slate-300"
          >
            <img
              src={`/flags/${flag}.svg`}
              width="24"
              height="16"
              alt=""
              aria-hidden="true"
            />
          </RouterLink>
        ))}
    </div>
  );
}
function PortfolioMenu() {
  const { tx } = useLang();
  const ref = useRef(null);
  return (
    <details
      ref={ref}
      className="relative"
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          ref.current.open = false;
          ref.current.querySelector("summary").focus();
        }
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget))
          ref.current.open = false;
      }}
    >
      <summary
        className={`${linkBase} flex cursor-pointer list-none items-center gap-2 py-3 text-slate-600`}
      >
        {tx("Portfolio", "Portföy")}
        <ChevronDown size={13} aria-hidden="true" />
      </summary>
      <div className="absolute left-0 top-full z-50 w-64 border border-slate-200 bg-white p-2 shadow-lg">
        <Link
          onClick={() => {
            ref.current.open = false;
          }}
          to="/#portfolio"
          className="focus-ring block px-4 py-3 text-sm hover:bg-paper"
        >
          {tx("View portfolio", "Portföyü inceleyin")}
        </Link>
        <Link
          onClick={() => {
            ref.current.open = false;
          }}
          to="/products/cernio"
          className="focus-ring block px-4 py-3 text-sm font-bold hover:bg-paper"
        >
          Cernio
        </Link>
        <Link
          onClick={() => {
            ref.current.open = false;
          }}
          to="/projects/dose-modernization"
          className="focus-ring block px-4 py-3 text-sm hover:bg-paper"
        >
          DOSE Modernization
        </Link>
        <Link
          onClick={() => {
            ref.current.open = false;
          }}
          to="/projects/kampanyambenim"
          className="focus-ring block px-4 py-3 text-sm hover:bg-paper"
        >
          KampanyamBenim
        </Link>
      </div>
    </details>
  );
}
export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { path } = languagePath(pathname);
  const { tx } = useLang();
  const menuButton = useRef(null);
  const navLink = (to, label, testid) => (
    <NavLink
      to={to}
      data-testid={testid}
      onClick={() => setOpen(false)}
      className={`${linkBase} ${path === to ? "text-ink" : "text-slate-500 hover:text-ink"}`}
    >
      {label}
    </NavLink>
  );
  const externalCta = (
    <a
      href={CERNIO_URL}
      className="focus-ring group inline-flex w-fit items-center gap-2 bg-ink px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-navy"
    >
      {tx("Explore Cernio", "Cernio'yu Keşfedin")}
      <ArrowUpRight size={14} aria-hidden="true" />
    </a>
  );
  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-slate-200/70 bg-white/80 backdrop-blur-md">
      <a href="#main-content" className="skip-link focus-ring">
        {tx("Skip to content", "İçeriğe geç")}
      </a>
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          data-testid="nav-logo-link"
          className="focus-ring flex items-center gap-2.5"
          onClick={() => setOpen(false)}
        >
          <img
            src="/logo.png"
            alt="GADULABS logo"
            width="28"
            height="28"
            className="h-7 w-7"
          />
          <span className="font-display text-lg font-bold tracking-tight text-ink">
            GADULABS
          </span>
        </Link>
        <nav
          className="hidden items-center gap-6 lg:flex"
          aria-label={tx("Primary", "Ana menü")}
        >
          <PortfolioMenu />
          {navLink("/company", tx("Company", "Şirket"), "nav-company-link")}
          <Link
            to="/company#contact"
            className={`${linkBase} text-slate-500 hover:text-ink`}
          >
            {tx("Contact", "İletişim")}
          </Link>
          <LangSwitch />
          {externalCta}
        </nav>
        <button
          ref={menuButton}
          type="button"
          data-testid="nav-mobile-menu-button"
          aria-label={
            open
              ? tx("Close menu", "Menüyü kapat")
              : tx("Open menu", "Menüyü aç")
          }
          aria-expanded={open}
          aria-controls="mobile-menu"
          onClick={() => setOpen((v) => !v)}
          className="focus-ring grid h-10 w-10 place-items-center border border-slate-200 text-ink lg:hidden"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            id="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="max-h-[calc(100dvh-4rem)] overflow-y-auto border-t border-slate-200/70 bg-white lg:hidden"
            aria-label={tx("Mobile", "Mobil menü")}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setOpen(false);
                menuButton.current.focus();
              }
            }}
          >
            <div className="flex flex-col gap-5 px-6 py-6">
              {navLink(
                "/#portfolio",
                tx("Portfolio", "Portföy"),
                "nav-portfolio-mobile",
              )}
              {navLink("/products/cernio", "Cernio", "nav-cernio-mobile")}
              {navLink(
                "/projects/dose-modernization",
                "DOSE Modernization",
                "nav-dose-mobile",
              )}
              {navLink(
                "/projects/kampanyambenim",
                "KampanyamBenim",
                "nav-kampanya-mobile",
              )}
              {navLink(
                "/company",
                tx("Company", "Şirket"),
                "nav-company-mobile",
              )}
              {navLink(
                "/company#contact",
                tx("Contact", "İletişim"),
                "nav-contact-mobile",
              )}
              <LangSwitch mobile />
              {externalCta}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
